import {
    createSlice,
    createSelector,
    createAsyncThunk,
    createEntityAdapter
  } from '@reduxjs/toolkit'
import { client } from '../../../../api/client'
import { addAlert } from '../alerts/alertSlice';
import { unlinkDiagramsByFolderId } from '../diagram/diagramSlice';
import { BASE_URL } from '../../../../config';
  
  const folderAdapter = createEntityAdapter()
  
  const initialState = folderAdapter.getInitialState({
    folderLoading: false,
    folderError: null,
    selectedId: null
  });  

  // Thunk 
  export const fetchFolders = createAsyncThunk('folders/fetchFolders', async () => {
    const response = await client.get(`${BASE_URL}/folder`);
    return response;
  });
  
  export const fetchFolder = createAsyncThunk(
    'folder/fetchFolder',
    async (form, { dispatch }) => {
      try {
        const { formValues } = form;
        const response = await client.get(
          `${BASE_URL}/folder/${formValues.id}`,
          { payload: formValues }
        );
    
        return response;
      } catch (err) {
        console.error('Error fetching folder:', err);
        throw err;
      }
    }
  );
  
  

export const updateFolder = createAsyncThunk(
  'folder/updateFolder',
  async (payload, { rejectWithValue }) => {
    const { id, label } = payload;
    try {
      const response = await client.put(
        `${BASE_URL}/folder/${id}/update`,
        { label },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      return { folder: response.data?.folder || { id, label } };
    } catch (err) {
      console.error('Error in updateFolder:', err);
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

  
  
  export const createFolder = createAsyncThunk('folder/createFolder', async (payload, { getState, dispatch }) => {
    try {
      const response = await client.post(`${BASE_URL}/folder/store`, payload)

      dispatch(addAlert({
        duration: 2000,
        severity: 'success',
        message: 'Folder stored successfully'
      }));         

      return { folder: response.data?.folder };
    } catch (err) {

      dispatch(addAlert({
        duration: 2000,
        severity: 'error',
        message: 'Error while storing the folder'
      }));     

      throw err;
    }
  });
  

export const deleteFolder = createAsyncThunk(
  'folder/deleteFolder',
  async (folderId, { dispatch, rejectWithValue }) => {
    try {
      if (!folderId) {
        throw new Error('No folder ID provided');
      }
  
        const response = await client.delete(
          `${BASE_URL}/folder/${folderId}/delete`,
          {
            data: {
              id: folderId,
            },
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        dispatch(unlinkDiagramsByFolderId(folderId));

        return { folder: response.data?.folder || { id: folderId } };
      } catch (err) {
        console.error('Error in deleteFolder:', err);
        // `rejectWithValue` allows you to return a custom error payload to the reducer
        return rejectWithValue(err.response?.data || err.message);
      }
    }
  );
  
  // Slice / Reducer
  const folderSlice = createSlice({
    name: 'folder',
    initialState,
    reducers: {
      setSelectedFolder(state, action) {
        state.selectedId = action.payload;
      }
    },
    extraReducers: builder => {
      builder
        .addCase(fetchFolders.pending, (state, action) => {
            state.folderLoading = true;
            state.folderError = null;
        })
        .addCase(fetchFolders.fulfilled, (state, action) => {
            folderAdapter.setAll(state, action.payload);
            state.folderLoading = false;
        })
        .addCase(fetchFolders.rejected, (state, action) => {
            state.folderLoading = false;
            state.folderError = action.error.message;
        })
        .addCase(createFolder.fulfilled, (state, action) => {
          const newFolder = action.payload.folder;
          folderAdapter.upsertOne(state, newFolder);
        })
        .addCase(updateFolder.pending, (state, action) => {
          const { id, label } = action.meta.arg;
          if (state.entities[id]) {
            state.entities[id].label = label;
          }
        })
        .addCase(updateFolder.fulfilled, (state, action) => {
          const updated = action.payload.folder;
          folderAdapter.upsertOne(state, updated);
        })
        .addCase(deleteFolder.pending, (state, action) => {
          folderAdapter.removeOne(state, action.meta.arg);
        })
        .addCase(deleteFolder.fulfilled, (state, action) => {
          const deleted = action.payload.folder;
          folderAdapter.removeOne(state, deleted.id);
        })
        .addCase(fetchFolder.pending, (state) => {
          state.folderLoading = true;
          state.folderError = null;
        })
        .addCase(fetchFolder.fulfilled, (state, action) => {
          folderAdapter.upsertOne(state, action.payload);
          state.selectedId = action.payload.id;
          state.folderLoading = false;
        })
        .addCase(fetchFolder.rejected, (state, action) => {
          state.folderLoading = false;
          state.folderError = action.error.message;
        });        
    }
  });
  
  export const {
    setSelectedFolder
  } = folderSlice.actions;
  
  export default folderSlice.reducer;

  // Selectors
  export const { 
    selectAll: selectFolders, 
    selectById: selectFolderById 
  } = folderAdapter.getSelectors(state => state.folder);

  export const selectSelectedFolder = (state) =>
    selectFolderById(state, state.folder.selectedId);
